import { ApiService, type ApiRequestConfig } from "./api.service";
import { uploadToPresignedUrl } from "./presigned-upload";
import type { ExportStatus } from "@/components/data-table/use-csv-export";
import type {
  TInaccessibleProject,
  TProject,
  TProjectMembership,
  TWorkspaceProjectRow,
  TWorkspaceProjectsListQuery,
  TWorkspaceProjectsPage,
} from "@/types/project";
import type { TBulkOperationStatus, TBulkSelection } from "@/types/bulk-selection";
import type { components } from "@/types/api.generated";
import type { IPriorityBreakdown } from "@/types/request-prioritization";

export type TProjectRestrictionImpact = components["schemas"]["ProjectRestrictionImpact"];
export type TProjectOutsideAssignees = components["schemas"]["ProjectOutsideAssignees"];
import type {
  TProjectImportCommitResult,
  TProjectImportDefinition,
  TProjectImportDryRunResult,
} from "@/types/project-import";

/** One staged row of the project CSV import, as the wizard sends it — the
 *  backend's own contract (`RawProjectImportRow`), keyed by definition field
 *  key so a `cf_<id>` column reaches the API the same way a core one does. */
interface TProjectImportRowPayload {
  source_row: number;
  values: Record<string, unknown>;
}

const FULL_LIST_PER_PAGE = 200;

function buildListQuery(params: TWorkspaceProjectsListQuery): string {
  const search = new URLSearchParams();
  if (params.archived !== undefined) search.set("archived", params.archived ? "true" : "false");
  if (params.q) search.set("q", params.q);
  for (const key of [
    "state",
    "category",
    "lead",
    "delivery_teams",
    // NG-2163: the Home team (scalar FK), same allow-list trap as the rest.
    "home_team",
    "client_teams",
    "programs",
    // NG-619. Same trap as the two below — the store can build a perfectly good
    // goals query that never reaches the server unless it is named here.
    "goals",
    // NG-713. This list is an ALLOW-LIST: a param missing here is dropped
    // silently, so the store can build a perfectly good query the server never
    // sees. That is what happened to these two.
    "process",
    "stage",
  ] as const) {
    const value = params[key];
    if (value && value.length > 0) search.set(key, value.join(","));
  }
  // Repeatable, not comma-joined: the backend reads getlist("custom_field")
  // and a value may itself contain what a comma-split would mangle.
  for (const entry of params.custom_field ?? []) search.append("custom_field", entry);
  if (params.order_by) search.set("order_by", params.order_by);
  if (params.group_by) search.set("group_by", params.group_by);
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.per_page != null) search.set("per_page", String(params.per_page));
  const str = search.toString();
  return str ? `?${str}` : "";
}

/** What `projects/<id>/lead-connection/` answers (NG-2967, NG-3080, NG-3175). */
export type TProjectLeadConnection = {
  unconnectedLeadIds: string[];
  connectedUserIds: string[];
  /** Who the Lead picker offers: the connected people eligible to lead, and the Leads. */
  leadCandidateIds: string[];
};

export class ProjectService extends ApiService {
  async list(
    slug: string,
    params: TWorkspaceProjectsListQuery = {},
    config?: ApiRequestConfig,
  ): Promise<TWorkspaceProjectsPage> {
    return this.get(`/api/workspaces/${slug}/projects/${buildListQuery(params)}`, config);
  }

  /** The importable field set the CSV wizard maps columns against (NG-1674):
   *  the core project columns plus one entry per active custom field, served by
   *  the same resolver the import validates rows with. Gated `project.create`
   *  OR `workspace.edit` on the backend. */
  async getImportDefinition(slug: string): Promise<TProjectImportDefinition> {
    return this.get<TProjectImportDefinition>(
      `/api/workspaces/${slug}/projects/import-definition/`,
    );
  }

  /** The Review step's read-only pre-commit check (NG-1673): the real
   *  resolve/validate logic over the real workspace references, writing
   *  nothing. Same gate as {@link getImportDefinition}. */
  async dryRunProjectImport(
    slug: string,
    rows: TProjectImportRowPayload[],
  ): Promise<TProjectImportDryRunResult> {
    return this.post<TProjectImportDryRunResult>(
      `/api/workspaces/${slug}/projects/import/dry-run/`,
      { rows },
    );
  }

  /** The Import click: one project per valid row, each in its own savepoint.
   *  Partial success is the norm and is reported per row. */
  async commitProjectImport(
    slug: string,
    rows: TProjectImportRowPayload[],
  ): Promise<TProjectImportCommitResult> {
    return this.post<TProjectImportCommitResult>(`/api/workspaces/${slug}/projects/import/`, {
      rows,
    });
  }

  /**
   * Export the projects list as CSV, scoped to the same filters/sort as the
   * list (`params`) and, when `body.ids` is set, restricted to those rows.
   * Returns the raw Response (200 stream / 202 async token).
   */
  async exportProjects(
    slug: string,
    body: { ids?: string[]; columns?: string[] },
    params: TWorkspaceProjectsListQuery = {},
  ): Promise<Response> {
    return this.postRaw(`/api/workspaces/${slug}/export-projects/${buildListQuery(params)}`, body);
  }

  async getExportStatus(slug: string, token: string): Promise<ExportStatus> {
    return this.get(`/api/workspaces/${slug}/export-status/${token}/`);
  }

  /** Who a switch to Team restricted would take this project away from (NG-2965).
   *  `applies` is false while the workspace's project-privacy flag is off. */
  async getRestrictionImpact(slug: string, projectId: string): Promise<TProjectRestrictionImpact> {
    return this.get<TProjectRestrictionImpact>(
      `/api/workspaces/${slug}/projects/${projectId}/restriction-impact/`,
    );
  }

  /** Which of `userIds` can't read the project apart from their own items (NG-2976,
   *  spec 12.6): the assignees the task page warns the assigner about. `applies` is
   *  false while the workspace's outside-assignee flag is off. */
  async getOutsideAssignees(
    slug: string,
    projectId: string,
    userIds: readonly string[],
  ): Promise<TProjectOutsideAssignees> {
    return this.get<TProjectOutsideAssignees>(
      `/api/workspaces/${slug}/projects/${projectId}/outside-assignees/`,
      { params: { users: userIds.join(",") } },
    );
  }

  /** Walks every page and flattens into a plain row list. Used by callers that
   *  legitimately need the full live list (sidebar pickers, dialogs). `config`
   *  goes to every page's request, so a query's abort signal stops the walk. */
  async fetchAll(
    slug: string,
    params: TWorkspaceProjectsListQuery = {},
    config?: ApiRequestConfig,
  ): Promise<TWorkspaceProjectRow[]> {
    const all: TWorkspaceProjectRow[] = [];
    let cursor: string | undefined;
    while (true) {
      const page = await this.list(
        slug,
        { ...params, per_page: params.per_page ?? FULL_LIST_PER_PAGE, cursor },
        config,
      );
      all.push(...page.results);
      if (!page.next_page_results || !page.next_cursor) break;
      cursor = page.next_cursor;
    }
    return all;
  }

  /** The workspace's ACTIVE projects the caller CANNOT access, anonymized as
   *  "ghost" rows for the plan-work Gantt (NG-789) — the viewer sees that time is
   *  committed without any detail. Empty for an admin (reads everything) or a
   *  non-staff caller. Deliberately separate from `list`/`fetchAll` so a ghost
   *  never leaks onto the general Projects list, the export, or a picker. */
  async fetchInaccessible(slug: string): Promise<TInaccessibleProject[]> {
    return this.get(`/api/workspaces/${slug}/projects/inaccessible/`);
  }

  async getProject(slug: string, id: string, config?: ApiRequestConfig): Promise<TProject> {
    return this.get(`/api/workspaces/${slug}/projects/${id}/`, config);
  }

  /** Who is connected to the project (spec 5.1, NG-3080), who of them may lead it
   *  (NG-3175), and its Leads who are not connected (NG-2967, spec 5.2); all empty
   *  with the flag off. An API from before `connected_user_ids` answers without it,
   *  which reads as nobody connected; one from before `lead_candidate_ids` offers the
   *  connected people, as it did. */
  async getLeadConnection(slug: string, id: string): Promise<TProjectLeadConnection> {
    const data = await this.get<Partial<components["schemas"]["ProjectLeadConnection"]>>(
      `/api/workspaces/${slug}/projects/${id}/lead-connection/`,
    );
    return {
      unconnectedLeadIds: data?.unconnected_lead_ids ?? [],
      connectedUserIds: data?.connected_user_ids ?? [],
      leadCandidateIds: data?.lead_candidate_ids ?? data?.connected_user_ids ?? [],
    };
  }

  /** The project's own priority score, line by line (NG-3071): what the Plan
   *  Work timeline's bar opens. `breakdown` is null only while scoring is off. */
  async getPriorityBreakdown(
    slug: string,
    id: string,
    config?: ApiRequestConfig,
  ): Promise<{ breakdown: IPriorityBreakdown | null }> {
    return this.get(`/api/workspaces/${slug}/projects/${id}/priority-breakdown/`, config);
  }

  async createProject(slug: string, data: Partial<TProject>): Promise<TProject> {
    return this.post(`/api/workspaces/${slug}/projects/`, data);
  }

  async updateProject(slug: string, id: string, data: Partial<TProject>): Promise<TProject> {
    return this.patch(`/api/workspaces/${slug}/projects/${id}/`, data);
  }

  async deleteProject(slug: string, id: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/projects/${id}/`);
  }

  /**
   * Delete projects selected from the workspace Projects list. Takes the
   * shared selection envelope (included / all_but_excluded / all) so a future
   * "select all matching" needn't ship every id. Returns the raw Response:
   * `200 {deleted, skipped}` for a small selection, or `202 {token, total,
   * skipped}` when the server hands off to a background job. The server
   * authorizes `project.delete` per project and only deletes the subset the
   * actor may delete (the rest come back as `skipped`).
   */
  async bulkDeleteProjects(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
  ): Promise<Response> {
    return this.deleteRaw(`/api/workspaces/${slug}/bulk-delete-projects/`, { selection });
  }

  /**
   * Set the status on projects selected from the workspace Projects list.
   * Returns the raw Response: `200 {updated, skipped, skipped_reasons}` for a
   * small selection, or `202 {token, total, skipped}` when the server hands
   * off to a background job. The server authorizes `project.edit` per project
   * and only applies the change to projects whose workflow allows the direct
   * transition (the rest come back as `skipped`, broken down by reason).
   */
  async bulkSetProjectStatus(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
    stateId: string,
  ): Promise<Response> {
    return this.postRaw(`/api/workspaces/${slug}/bulk-set-status-projects/`, {
      selection,
      state: stateId,
    });
  }

  /**
   * Archive projects selected from the workspace Projects list. Returns the
   * raw Response: `200 {archived, skipped, skipped_reasons}` for a small
   * selection, or `202 {token, total, skipped}` when the server hands off to
   * a background job. The server authorizes `project.edit` per project and
   * archives only the authorized subset (the rest come back as `skipped`).
   */
  async bulkArchiveProjects(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
  ): Promise<Response> {
    return this.postRaw(`/api/workspaces/${slug}/bulk-archive-projects/`, { selection });
  }

  /**
   * Assign (or unassign) one user as project.lead across projects selected
   * from the workspace Projects list. Returns the raw Response: `200
   * {updated, skipped, skipped_reasons}` for a small selection, or `202
   * {token, total, skipped}` when the server hands off to a background job.
   * The server authorizes `project.edit` per project and applies the change
   * only to the authorized subset (the rest come back as `skipped`).
   */
  async bulkAssignProjectLeads(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
    userId: string,
    assign: boolean,
  ): Promise<Response> {
    return this.postRaw(`/api/workspaces/${slug}/bulk-assign-project-leads/`, {
      selection,
      user_id: userId,
      assign,
    });
  }

  /** Poll a long-running bulk operation's progress by token. Same endpoint
   *  work items' bulk actions poll — the status row carries no domain
   *  assumption, so both reuse it unchanged. */
  async getBulkOperationStatus(slug: string, token: string): Promise<TBulkOperationStatus> {
    return this.get(`/api/workspaces/${slug}/bulk-operations/${token}/`);
  }

  /** Request cancellation of a long-running bulk operation by token — same
   *  domain-agnostic endpoint family as the status poll above. Flips a
   *  QUEUED/PROCESSING operation to CANCELLING; the worker finishes whatever
   *  item it's mid-transaction on, then moves the row to CANCELLED itself. */
  async cancelBulkOperation(
    slug: string,
    token: string,
  ): Promise<{ token: string; status: string }> {
    return this.post(`/api/workspaces/${slug}/bulk-operations/${token}/cancel/`, {});
  }

  async listMembers(slug: string, id: string): Promise<TProjectMembership[]> {
    return this.get(`/api/workspaces/${slug}/projects/${id}/members/`);
  }

  async memberMe(slug: string, id: string): Promise<TProjectMembership> {
    return this.get(`/api/workspaces/${slug}/projects/${id}/project-members/me/`);
  }

  async checkIdentifier(slug: string, name: string): Promise<{ exists: boolean }> {
    const res = await this.get<{ name: string; available: boolean }>(
      `/api/workspaces/${slug}/project-identifiers?name=${encodeURIComponent(name)}`,
    );
    return { exists: !res.available };
  }

  async archiveProject(slug: string, projectId: string): Promise<{ archived_at: string }> {
    return this.post(`/api/workspaces/${slug}/projects/${projectId}/archive/`, {});
  }

  async unarchiveProject(slug: string, projectId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/projects/${projectId}/archive/`);
  }

  async updateProjectMetadata(
    slug: string,
    projectId: string,
    data: { start_date?: string | null; end_date?: string | null; is_draft?: boolean },
  ): Promise<{
    id: string;
    project: string;
    is_draft: boolean;
    start_date: string | null;
    end_date: string | null;
  }> {
    return this.patch(`/api/workspaces/${slug}/projects/${projectId}/metadata/`, data);
  }

  async listAttachments(
    slug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TProjectAttachment[]> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/attachments/`, config);
  }

  async uploadAttachment(slug: string, projectId: string, file: File): Promise<TProjectAttachment> {
    const { asset_id, upload_data } = await this.post<TProjectAttachmentUploadResponse>(
      `/api/workspaces/${slug}/projects/${projectId}/attachments/`,
      { name: file.name, size: file.size, type: file.type },
    );

    await uploadToPresignedUrl(upload_data, file);

    return this.patch(`/api/workspaces/${slug}/projects/${projectId}/attachments/${asset_id}/`, {});
  }

  async deleteAttachment(slug: string, projectId: string, attachmentId: string): Promise<void> {
    return this.delete(
      `/api/workspaces/${slug}/projects/${projectId}/attachments/${attachmentId}/`,
    );
  }
}

interface TProjectAttachmentUploadResponse {
  asset_id: string;
  asset_url: string;
  upload_data: {
    url: string;
    method?: "PUT" | "POST";
    fields?: Record<string, string>;
  };
}

export interface TProjectAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  asset_url: string | null;
  // External target for link rows (kind="link"); null for files.
  url: string | null;
  created_at: string | null;
  source: "intake_submission" | "work_item" | null;
  source_label: string | null;
  kind: "file" | "link";
}
